import { AbsgArrayHelper, AppInsights, CacheService, CamelMapHelper, LogService, SessionContextService } from '@absg/angular-common';
import { ODataResult } from '@absg/angular-components';
import { AuthenticationService, PartyContextService } from '@absg/angular-platform';
import { UserStationsApi } from '@absg/api-entitlements';
import { AssignedProductDetail, GetCheapestEquivilantRequest, ProductDetail } from '@absg/api-formulary';
import { InventoryApi, InventoryItemSummary, InventoryItemUth, PositionContainer } from '@absg/api-inventory';
import { Clinic, ClinicApi, ClinicLite, ClinicODataApi, ODataResultClinic, PracticeClinicDetail, PracticeProperties } from '@absg/api-party';
import { PurchasingApi } from '@absg/api-purchase-order';
import {
  AccessPoint, RequestContainerDetails, ResponseContainerDetails,
  RuleContainerSettingApi, Station, StationApi, StationDetails, StationLite, StationODataApi, StationRuleValue, UpdateStationsRuleValueRequest,
} from '@absg/api-station-management';
import {
  BaseService, CacheKeys, CommonDataService as SharedCommonDataService, Constants, ContainerTypes, EnvironmentService, InventoryItem,
  InventoryItemUnderHood, LookupHelper, NimAlertService, PartyService, Patient, PatientDispenseItemViewModel,
  PrintingTemplateService, RuleContainerSetting, Rules, RuleService, SessionService, stationConstants,
  StationInfoService as SharedStationInfoService,
  StorageAccessPoint, StorageCabinet, StoragePosition,
  StorageStation, StringConstants, SupplierInfo,
} from '@absg/nim-client-shared';
import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, combineLatest, EMPTY, forkJoin, Observable, of } from 'rxjs';
import {
  catchError, concatMap, finalize, map, switchMap, take, tap,
} from 'rxjs/operators';
import { ElectronApplicationService } from 'shared.services/electron-application.service';
import { StorageStationService } from 'shared.services/storage-station.service';

import { AccessPointService } from './access-point/access-point.service';
import { LockAccessService } from './access-point/lock-access.service';
import { CommonDataService } from './common-data.service';
import { EnvironmentService as StationEnvironmentService } from './environment.service';

@Injectable({ providedIn: 'root' })
export class StationInfoService extends BaseService implements OnDestroy {
  appVersion = new BehaviorSubject<string>(null);

  ipcRenderer: Electron.IpcRenderer;
  private isAccessDenied = new BehaviorSubject<boolean>(false);
  private selectedPatientInternal: Patient;
  private packageDirectory: string;
  private isPackaged: boolean;

  get practiceExpirationDateWarningDay(): number {
    return this.sharedStationInfoService.practiceExpirationDateWarningDay;
  }
  set practiceExpirationDateWarningDay(value: number) {
    this.sharedStationInfoService.practiceExpirationDateWarningDay = value;
  }
  get practiceMinCharSearch(): number {
    return this.sharedStationInfoService.practiceMinCharSearch;
  }
  set practiceMinCharSearch(value: number) {
    this.sharedStationInfoService.practiceMinCharSearch = value;
  }

  get hostName(): BehaviorSubject<string> {
    return this.sharedStationInfoService.hostName;
  }
  set hostName(value: BehaviorSubject<string>) {
    this.sharedStationInfoService.hostName = value;
  }

  get station(): BehaviorSubject<StorageStation> {
    return this.sharedStationInfoService.station;
  }
  set station(value: BehaviorSubject<StorageStation>) {
    this.sharedStationInfoService.station = value;
  }

  get stationId(): BehaviorSubject<number> {
    return this.sharedStationInfoService.stationId;
  }
  set stationId(value: BehaviorSubject<number>) {
    this.sharedStationInfoService.stationId = value;
  }

  get practiceClinicDetail(): BehaviorSubject<PracticeClinicDetail> {
    return this.sharedStationInfoService.practiceClinicDetail;
  }
  set practiceClinicDetail(value: BehaviorSubject<PracticeClinicDetail>) {
    this.sharedStationInfoService.practiceClinicDetail = value;
  }

  get supplierList(): BehaviorSubject<SupplierInfo[]> {
    return this.sharedStationInfoService.supplierList;
  }
  get loadingData(): BehaviorSubject<boolean> {
    return this.sharedStationInfoService.loadingData;
  }
  set loadingData(value: BehaviorSubject<boolean>) {
    this.sharedStationInfoService.loadingData = value;
  }

  get isInventoryLoading(): boolean {
    return this.sharedStationInfoService.isInventoryLoading;
  }
  set isInventoryLoading(value: boolean) {
    this.sharedStationInfoService.isInventoryLoading = value;
  }
  get isInventoryCacheExpired(): boolean {
    return this.sharedStationInfoService.isInventoryCacheExpired;
  }
  set isInventoryCacheExpired(value: boolean) {
    this.sharedStationInfoService.isInventoryCacheExpired = value;
  }
  get isUnassignedLoading(): boolean {
    return this.sharedStationInfoService.isUnassignedLoading;
  }
  set isUnassignedLoading(value: boolean) {
    this.sharedStationInfoService.isUnassignedLoading = value;
  }
  get itemsReturnedWithUnknownLot(): PatientDispenseItemViewModel[] {
    return this.sharedStationInfoService.itemsReturnedWithUnknownLot;
  }
  set itemsReturnedWithUnknownLot(value: PatientDispenseItemViewModel[]) {
    this.sharedStationInfoService.itemsReturnedWithUnknownLot = value;
  }
  get restockItem(): InventoryItemSummary {
    return this.sharedStationInfoService.restockItem;
  }
  set restockItem(value: InventoryItemSummary) {
    this.sharedStationInfoService.restockItem = value;
  }

  constructor(
    private logService: LogService,
    private cacheService: CacheService,
    private electronApplicationService: ElectronApplicationService,
    private authenticationService: AuthenticationService,
    private partyService: PartyService,
    private storageStationService: StorageStationService,
    private ruleService: RuleService,
    private clinicApi: ClinicApi,
    private clinicODataApi: ClinicODataApi,
    private alertService: NimAlertService,
    private zone: NgZone,
    private sessionContext: SessionContextService,
    private partyContextService: PartyContextService,
    private userStationsApi: UserStationsApi,
    private stationApi: StationApi,
    private stationODataApi: StationODataApi,
    private inventoryApi: InventoryApi,
    private purchasingApi: PurchasingApi,
    private accessPointService: AccessPointService,
    private appInsights: AppInsights,
    private ruleContainerSettingApi: RuleContainerSettingApi,
    private commonDataService: CommonDataService,
    private printingService: PrintingTemplateService,
    private lockService: LockAccessService,
    private sessionService: SessionService,
    private environmentService: EnvironmentService,
    private stationEnvironmentService: StationEnvironmentService,
    private sharedCommonDataService: SharedCommonDataService,
    private sharedStationInfoService: SharedStationInfoService,
  ) {
    super(logService);
    // listen to shared events here
    // setVirtualDoors$
    // getAllFailedDoors$
  }

  ngOnDestroy(): void {
    this.itemsReturnedWithUnknownLot = null;
  }

  isValidName(hostName: string): boolean {
    return hostName !== undefined && hostName.length > 0;
  }

  getStationId(): number {
    const station = this.station.getValue();
    return station ? station.id : 0;
  }

  getStationName(): string {
    const station = this.station.getValue();
    return station ? station.name : '';
  }

  requestHostName(): void {
    if (this.electronApplicationService.isRunningElectron()) {
      this.ipcRenderer = this.electronApplicationService.getIpcRenderer();
       
      this.ipcRenderer.on('hostname-response', (_event, args) => this.publishHostName(args));
       
      this.ipcRenderer.on('version-response', (_event, args) => this.publishAppVersion(args));
      this.ipcRenderer.on('directory-response', (_event, args) => this.publishPackageDirectory(args));
      this.ipcRenderer.on('packaged-response', (_event, args) => this.publishIsPackaged(args));

      // initiate a request to get the machine name from Electron
      this.ipcRenderer.send('hostname-request');
      this.ipcRenderer.send('version-request');
      this.ipcRenderer.send('packaged-request');
      this.ipcRenderer.send('directory-request');
    } else {
      // set hostname, set version
      this.publishHostName('ctrl+alt+t to switch station');
      this.environmentService.setUseApiPrinting(true);
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      const pkgJson = require('../../../../../package.json');
      this.publishAppVersion(pkgJson.version);
    }
  }

  publishHostName(hostName: string): void {
    this.zone.run(() => {
      if (this.isValidName(hostName)) {
        // hostName = 'UAC6'; // added for testing purpose
        this.hostName.next(hostName);
        this.checkAuthenticationAndLoadStation();
        this.sessionContext.setMachineName(hostName);
        this.appInsights.setMachineName(hostName);
      }
    });
  }

  publishAppVersion(appVersion: string): void {
    this.zone.run(() => {
      if (this.isValidName(appVersion)) {
        this.appVersion.next(appVersion);
        this.sessionContext.setApplicationVersion(appVersion);
      }
    });
  }

  publishPackageDirectory(directory: string): void {
    this.zone.run(() => {
      this.packageDirectory = directory;
    });
  }

  publishIsPackaged(isPackaged: boolean): void {
    this.zone.run(() => {
      this.isPackaged = isPackaged;
    });
  }

  setLoggedInUserName(): void {
    this.sharedStationInfoService.setLoggedInUserName();
  }

  getLoggedInUserName(): string {
    return this.sharedStationInfoService.getLoggedInUserName();
  }

  getLoggedInUserId(): number {
    return this.sharedStationInfoService.getLoggedInUserId();
  }

  getIsLoggedIn(): boolean {
    return this.sharedStationInfoService.getIsLoggedIn();
  }

  get selectedPatient(): Patient {
    return this.selectedPatientInternal;
  }

  set selectedPatient(currentPatient: Patient) {
    this.selectedPatientInternal = currentPatient;
  }

  refreshStation(): Observable<StationDetails> {
    const stationInfo = this.station.getValue();
    return this.storageStationService.getStationDetailsForLoading(stationInfo.name)
      .pipe(take(1),
        tap((stationDetails: StationDetails) => {
          if (stationDetails) {
            this.station.next(stationDetails as StorageStation);
          } else {
            this.station.next(stationInfo);
          }
        }),
        catchError((error) => {
          this.onError(error);
          return EMPTY;
        }),
      );
  }

  checkAuthenticationAndLoadStation(): void {
    const isLoggedIn = this.authenticationService.isLoggedIn$;
    const hasAcceptedDisclaimer = this.authenticationService.isLegalDisclaimerAccepted();

    combineLatest([isLoggedIn, hasAcceptedDisclaimer])
      .pipe(
        map(([isUserLoggedIn, hasUserAcceptedDisclaimer]) => ({
          isLoggedIn: isUserLoggedIn,
          hasAcceptedDisclaimer: hasUserAcceptedDisclaimer,
        })))
      .subscribe((userStatus) => {
        const loggedIn = userStatus.isLoggedIn && userStatus.hasAcceptedDisclaimer;
        if (this.sharedStationInfoService.getIsLoggedIn() !== loggedIn) {
          this.sharedStationInfoService.setIsLoggedIn(loggedIn);
          if (loggedIn) {
            this.authenticationService.setIsUserLoggedInToStation(true);
            this.partyContextService.getStationPartyContextId()
              .pipe(take(1))
              .subscribe(partyContext => {
                if (partyContext) {
                  this.loadStation();
                }
              });
          } else {
            setTimeout(() => {
              this.isInventoryCacheExpired = true;
              this.clearCachedData();
            }, 500);
          }
        }
      });
  }

  private clearCachedData(): void {
    this.clearInventoryCache();
    this.ruleService.clearStationRuleSettingsCache();
    this.clearStationRules();
  }

  loadStation(): void {
    const hostName = this.hostName.getValue();
    if (hostName) {
      let stationInfo: StorageStation;
      this.loadingData.next(true);

      forkJoin([
        this.storageStationService.getStationDetailsForLoading(hostName),
        this.storageStationService.getStationInfoDetailsForLoading(hostName, this.stationId.value),
      ]).pipe(
        take(1),
        switchMap(([stationDetails, loadPayload]) => {
          stationInfo = stationDetails as StorageStation;
          this.station.next(stationInfo);
          this.stationEnvironmentService.setExternalConfig(loadPayload.stationConfigRules);
          // Refresh/Sync doors that might be mismatched
          this.lockService.setVirtualDoors(stationDetails);

          if (!stationDetails) {
            const errorMessage = `${hostName} is not registered as a valid station`;
            this.alertService.showErrorAlert('Station Not Found', new Error(errorMessage));
            this.handleErrorMessage(errorMessage);
            return of();
          }
          const foundPhysicalCabinet =
            AbsgArrayHelper.find(stationDetails.cabinets, (cab) => cab?.containerType?.id !== ContainerTypes.Virtual
              && cab?.containerType?.id !== ContainerTypes.VirtualPrintModule
              && cab?.containerType?.id !== ContainerTypes.LocksAndPrintingModule); // LPM is physical, but without remote locks, there is nothing to check
          if (!!foundPhysicalCabinet) {
            this.accessPointService.getAllFailedDoor();
          }
          return forkJoin([
            this.refreshInventoryCacheAsync(),
            this.getClinicAndPracticeInfo(stationInfo.facilityId),
            this.getSupplierList(),
            this.getPracticeProperties(),
            this.commonDataService.getTransactionTypes(),
            this.commonDataService.getActivityTypes(),
          ]);
        }),
        concatMap((result) => {
          this.sharedCommonDataService.transactionTypesMap = this.commonDataService.transactionTypesMap;
          this.sharedCommonDataService.activityTypesMap = this.commonDataService.activityTypesMap;
          this.sharedStationInfoService.practiceClinicDetail.next(this.practiceClinicDetail.getValue());
          this.sharedStationInfoService.station.next(this.station.getValue());
          const practiceLite = this.sessionService.practiceLite.value;
          return this.printingService.getTemplateStatus(practiceLite).pipe(
            take(1),
            map(() => result));
        }),
        finalize(() => this.loadingData.next(false)),
      ).subscribe();
    } else {
      const errorMessage = 'No valid machine name found';
      this.alertService.showErrorAlert('Invalid Station', new Error(errorMessage));
      this.handleErrorMessage(errorMessage);
    }
  }

  getPracticeExpirationDateWarningDay(): number {
    return this.practiceExpirationDateWarningDay;
  }

  getPracticeMinimumCharacterSearch(): number {
    return this.practiceMinCharSearch;
  }

  private getPracticeProperties(): Observable<PracticeProperties> {
    return this.partyService.getPracticeProperties()
      .pipe(
        take(1),
        map(response => {
          this.practiceExpirationDateWarningDay = response.expirationDateWarning;
          this.practiceMinCharSearch = response.minimumCharacterSearch;
          return response;
        }));
  }

  getClinicAndPracticeInfo(facilityId: number): Observable<void> {
    return this.clinicApi.getPracticeClinicDetailByFacilityId(facilityId)
      .pipe(
        take(1),
        tap((practiceClinicDetail) => {
          if (practiceClinicDetail) {
            let result: PracticeClinicDetail;
            if (practiceClinicDetail['PracticeId']) { // party sends pascal case - map to camel
              result = CamelMapHelper.camelizeKeys(practiceClinicDetail) as PracticeClinicDetail;
              // csr isn't handled correctly, so set them
              result.externalCSRId = practiceClinicDetail['ExternalCSRId'];
              result.cSRFirstName = practiceClinicDetail['CSRFirstName'];
              result.cSRLastName = practiceClinicDetail['CSRLastName'];
              result.cSRPhoneNumber = practiceClinicDetail['CSRPhoneNumber'];
            } else {
              result = practiceClinicDetail;
            }

            this.practiceClinicDetail.next(result);
          }
        }),
        catchError(error => {
          this.onError(error);
          return of();
        }),
        map(() => { }),
      );
  }

  getClinicsInfoByPracticeId(): Observable<Array<Clinic>> {
    const cachedPracticeClinics = this.cacheService.getWithExpiry(CacheKeys.practiceClinics);
    if (cachedPracticeClinics.value) {
      return of(cachedPracticeClinics.value);
    } else {
      const practice = this.practiceClinicDetail.value;
      return this.clinicODataApi.getClinicsByPracticeIdOData(practice.practiceId)
        .pipe(
          catchError(error => {
            this.handleErrorMessage(error);
            return of([]);
          }),
          map((response: ODataResultClinic) => response.value),
          map(results => {
            this.cacheService.setWithExpiry(CacheKeys.practiceClinics, results, CacheKeys.eightHoursInMinutes);
            return results;
          }),
        );
    }
  }

  getPracticeId(): number {
    const practice = this.practiceClinicDetail.value;
    return practice.practiceId;
  }

  getSupplierList(): Observable<void> {
    return this.partyService.getSuppliers()
      .pipe(
        take(1),
        tap(suppliers => this.supplierList.next(CamelMapHelper.camelizeKeys(suppliers))),
        catchError(error => {
          this.onError(error);
          return of();
        }),
        map(() => { }),
      );
  }

  getAccessPointById(accessPointId: number): AccessPoint {
    const foundLad = AbsgArrayHelper.find(this.station.getValue().limitedAccessDrawers, lad => lad.id === accessPointId);
    if (foundLad) {
      return foundLad;
    }
    const accessPointDetails = AbsgArrayHelper.reduce(this.station.getValue().cabinets,
      (accessPoints: StorageAccessPoint[], cabinet: StorageCabinet) => accessPoints.concat(cabinet.children), []);
    const accessPoint = AbsgArrayHelper.find(accessPointDetails, point => point?.id === accessPointId);
    if (accessPoint === undefined || accessPoint === null) {
      const errorMessage = `Access point ${accessPointId} could not be found`;
      this.alertService.showErrorAlert('Error', new Error(errorMessage));
      this.handleErrorMessage(errorMessage);
    }
    return accessPoint;
  }

  getAccessPointByName(name: string): AccessPoint {
    const accessPointDetails = AbsgArrayHelper.reduce(this.station.getValue().cabinets,
      (accessPoints: StorageAccessPoint[], cabinet: StorageCabinet) => accessPoints.concat(cabinet.children), []);
    const accessPoint: StorageAccessPoint = AbsgArrayHelper.find(accessPointDetails, point => !!point && point.name === name);
    if (!accessPoint) {
      const errorMessage = `Access point ${name} could not be found`;
      this.alertService.showErrorAlert('Error', new Error(errorMessage));
      this.handleErrorMessage(errorMessage);
    }
    return accessPoint;
  }

  getPositionById(positionContainerId: number): StoragePosition {
    const accessPointDetails = this.station.value.cabinets
      .reduce((accessPoints: StorageAccessPoint[], cabinet: StorageCabinet) => accessPoints.concat(cabinet.children), [])
      .filter(pd => pd !== undefined && pd !== null);
    const stationPosition = this.getFlatPositionsFromStation(accessPointDetails);
    const position = AbsgArrayHelper.find(stationPosition, point => point && point.id === positionContainerId);
    if (position === undefined || position === null) {
      const errorMessage = `Position ${positionContainerId} could not be found`;
      this.alertService.showErrorAlert('Error', new Error(errorMessage));
      this.handleErrorMessage(errorMessage);
      return null;
    }
    let parent: StorageAccessPoint;
    if (position?.containerType?.id !== ContainerTypes.LimitedAccessDrawer) {
      parent = AbsgArrayHelper.find(accessPointDetails, (ac) => ac.id === position.parentContainerId);
    } else {
      parent = AbsgArrayHelper.find(this.station.value.limitedAccessDrawers, (ac) => ac.id === position.id);
    }
    position.isSimulateMode = parent?.isSimulateMode;
    return position;
  }

  getFlatPositionsFromStation(accessPointDetails: StorageAccessPoint[]): StoragePosition[] {
    return AbsgArrayHelper.flatMap(accessPointDetails, c =>
      !AbsgArrayHelper.isNil(c.children) ? AbsgArrayHelper.flatMap(c.children, a => a) : []);
  }

  getInventoryItem(supplierProductInclusionId: number): InventoryItemSummary {
    const inventoryItems = this.getAssignedInventoryItems().getValue();
    let inventoryItem: InventoryItemSummary;
    if (inventoryItems && inventoryItems.length > 0) {
      inventoryItem = AbsgArrayHelper.find(inventoryItems, item => item.supplierProductInclusionId === supplierProductInclusionId);
    }
    return inventoryItem;
  }

  getUnassignedPendingQtyByItemId(supplierProductInclusionId: number): number {
    const unassignedItems = this.getUnassignedItems().getValue();
    if (unassignedItems && unassignedItems != null && unassignedItems.length > 0) {
      const foundItem: InventoryItemSummary =
        AbsgArrayHelper.find(unassignedItems, item => item.supplierProductInclusionId === supplierProductInclusionId);
      return (foundItem != null && foundItem.pendingQuantity > 0) ? foundItem.pendingQuantity : 0;
    } else {
      return 0;
    }
  }

  getAssignedPosition(supplierProductInclusionId: number, inventoryItems: InventoryItemSummary[] = null,
    patientOwnedPatientId: number = null, patientOwnedItemTypeId: number = null): StoragePosition {
    let assignedPosition: StoragePosition;
    inventoryItems = AbsgArrayHelper.isNil(inventoryItems) ? this.getAssignedInventoryItems().getValue() : inventoryItems;
    const inventoryItem = AbsgArrayHelper.find(inventoryItems, item => item.supplierProductInclusionId === supplierProductInclusionId
      && item.patientId === patientOwnedPatientId && item.patientOwnedItemTypeId === patientOwnedItemTypeId);

    if (inventoryItem) {
      assignedPosition = this.getPositionById(inventoryItem.positionContainerId);
    }
    return assignedPosition;
  }

  getAssignedPositionBasedOnInclusions(supplierProductInclusionIdList: number[], inventoryItems: InventoryItemSummary[] = null,
    patientOwnedPatientId: number = null, patientOwnedItemTypeId: number = null): StoragePosition {
    let assignedPosition: StoragePosition;
    inventoryItems = AbsgArrayHelper.isNil(inventoryItems) ? this.getAssignedInventoryItems().getValue() : inventoryItems;
    AbsgArrayHelper.each(supplierProductInclusionIdList, supplierProductInclusionId => {
      const inventoryItem = AbsgArrayHelper.find(inventoryItems, item => item.supplierProductInclusionId === supplierProductInclusionId
        && item.patientId === patientOwnedPatientId && item.patientOwnedItemTypeId === patientOwnedItemTypeId);
      if (inventoryItem) {
        assignedPosition = this.getPositionById(inventoryItem.positionContainerId);
        return assignedPosition;
      }
    });
    return assignedPosition;
  }

  onError(error): void {
    if (!this.getAccessDenied()) {
      this.alertService.showErrorAlert('Error Loading Station Data.', new Error('Station data could not be loaded.'));
    }
    this.handleError(error);
  }

  //#region "Inventory Methods"
  // this needs to be an observable, if the value is null it will load at some point in the future
  // this is broken, use 'getCachedInventoryItems' instead
  getInventoryItems(): BehaviorSubject<InventoryItemSummary[]> {
    const key = CacheKeys.InventoryItemSummaries.toString();
    const data = this.cacheService.get(key);
    if (data.getValue() === null) {
      this.authenticationService.isLoggedIn$
        .pipe(take(1))
        .subscribe(
          isUserLoggedIn => {
            if (isUserLoggedIn) {
              this.partyContextService.getStationPartyContextId()
                .pipe(take(1))
                .subscribe(party => {
                  if (party) {
                    this.loadInventoryItemsForStation();
                  }
                });
            } else {
              const inventoryItems = new Array<InventoryItemSummary>();
              this.cacheService.set(key, inventoryItems);
            }
          },
          (error) => {
            this.onError(error);
          });
    }

    return data;
  }

  getCachedInventoryItems(): Observable<InventoryItemSummary[]> {
    const key = CacheKeys.InventoryItemSummaries.toString();
    const data = this.cacheService.get(key);
    if (data.getValue() === null) {
      return this.partyContextService.getStationPartyContextId()
        .pipe(take(1),
          concatMap(party => {
            if (party) {
              return this.loadInventoryItemsForStation();
            } else {
              return of(false);
            }
          }),
          map(() => this.cacheService.get(key).getValue()));
    }
    return of(data.getValue());
  }

  getAssignedInventoryItems(): BehaviorSubject<InventoryItemSummary[]> {
    return this.sharedStationInfoService.getAssignedInventoryItems();
  }

  setAssignedInventoryItems(items: InventoryItemSummary[]): void {
    this.sharedStationInfoService.setAssignedInventoryItems(items);
  }

  getInventoryItemsByCabinetId(cabinetId: number): InventoryItemSummary[] {
    const cabinet = this.getCabinet(cabinetId);
    const accessPointDetails = cabinet.children;
    return this.getInventoryItemsForContainer(accessPointDetails);
  }

  getInventoryItemsByAccessPointId(cabinetId: number, accessPointId: number): InventoryItemSummary[] {
    const cabinet = this.getCabinet(cabinetId);
    const accessPointDetails = cabinet.children.filter(c => c.id === accessPointId);
    return this.getInventoryItemsForContainer(accessPointDetails);
  }

  getAssignedInventoryItemsForLADD(laddDoor: StorageAccessPoint): InventoryItemSummary[] {
    return (laddDoor) ? this.getInventoryItemsForContainer([laddDoor]) : null;
  }

  getUnassignedItems(): BehaviorSubject<InventoryItemSummary[]> {
    const key = CacheKeys.InventoryPendingQuantityUnassignedItems.toString();
    const data = this.cacheService.get(key);
    if (data.getValue() === null) {
      this.loadPendingQuantitiesUnassignedItemsForStation();
    }
    return data;
  }

  loadPendingQuantitiesUnassignedItemsForStation(): void {
    if (!this.isUnassignedLoading) {
      this.isUnassignedLoading = true;
      const key = CacheKeys.InventoryPendingQuantityUnassignedItems.toString();

      const unassignedItems = new Array<InventoryItemSummary>();
      this.cacheService.set(key, unassignedItems);
      this.isUnassignedLoading = false;
    }
  }

  loadInventoryItemsForStation(): Observable<boolean> {
    return this.sharedStationInfoService.loadInventoryItemsForStation();
  }

  clearInventoryCache(): void {
    this.sharedStationInfoService.clearInventoryCache();
  }

  refreshInventoryCacheAsync(): Observable<void> {
    return this.sharedStationInfoService.refreshInventoryCacheAsync();
  }
  //#endregion "Inventory Methods"
  setStationSettings(settings: RuleContainerSetting[]): BehaviorSubject<RuleContainerSetting[]> {
    return this.ruleService.setStationSettings(settings);
  }

  getStationSettings(): Observable<RuleContainerSetting[]> {
    return this.ruleService.getStationSettings();
  }

  getLadsFromStation(): AccessPoint[] {
    return this.station.getValue().limitedAccessDrawers;
  }

  getLadById(containerId: number): AccessPoint {
    return AbsgArrayHelper.find(this.station.getValue().limitedAccessDrawers, lad => lad.id === containerId);
  }

  getLadsByParentId(containerId: number): AccessPoint[] {
    return AbsgArrayHelper.filter(AbsgArrayHelper.orderBy(this.station.getValue().limitedAccessDrawers,
      lad => lad.sequence), orderedLad => orderedLad.parentContainerId === containerId);
  }

  refreshStationDefaultRules(): void {
    this.ruleService.getStationDefaultRules();
  }
  //#endregion "Rule Setting Methods"

  setAccessDenied(isAccessDenied: boolean): void {
    this.isAccessDenied.next(isAccessDenied);
  }

  getAccessDenied(): BehaviorSubject<boolean> {
    return this.isAccessDenied;
  }

  userHasAccessToStation(): Observable<boolean> {
    const key = CacheKeys.userStationAccess;
    const data = this.cacheService.get(key);
    const trueValue = 'true';
    if (data.getValue() !== null && data.getValue() === trueValue) {
      return of(true);
    } else {
      return this.userStationsApi.validateUserStation()
        .pipe(
          map(hasAccess => {
            if (hasAccess === true) {
              this.cacheService.set(key, 'true');
              return true;
            }
            return false;
          }),
          take(1),
        );
    }
  }

  getUnderHoodContainerList(items: InventoryItemUth[]): Observable<ResponseContainerDetails> {
    const request = this.mapContainerDetails(items);
    return this.stationApi.getContainerDetails(request)
      .pipe(
        catchError(error => {
          this.handleErrorMessage(error);
          return of();
        }),
      );
  }

  mapUnderHoodStationList(invItems: InventoryItemUth[], response: ResponseContainerDetails,
    invUnit: string, clinics: ClinicLite[], witnessRequiredSetting = null): InventoryItemUnderHood[] {
    const stationItems = [];

    AbsgArrayHelper.each(invItems, (item) => {
      const inv = new InventoryItemUnderHood();
      const positionContainer = AbsgArrayHelper.filter(response.containerInfo, x => x.containerId === item.positionContainerId);
      const isBinRule = AbsgArrayHelper.find(positionContainer, x => x.ruleId === Rules.positionLevelRules.isBin
        && x.xrefValue === StringConstants.boolY);
      if (AbsgArrayHelper.isNil(isBinRule)) {
        const station = AbsgArrayHelper.find(response.stationInfo, x => x.stationId === item.stationContainerId);
        if (!station) {
          this.logService.logTrace(`item ${item.positionContainerId} has an invalid station id ${item.stationContainerId}`);
        }
        const clinic = AbsgArrayHelper.find(clinics, x => x.facilityId === station.facilityId);

        inv.underhoodQoh = item.underhoodQoh;
        inv.clinicName = clinic ? clinic.clinicCurrentName : null;
        inv.invUnits = invUnit;
        inv.stationName = station ? station.stationName : null;
        inv.isWitness = Constants.offText;

        const dispenseTypeRule = AbsgArrayHelper.find(positionContainer, x => x.ruleId === Rules.positionLevelRules.dispenseTypeId);

        if (!AbsgArrayHelper.isNil(dispenseTypeRule)) {
          inv.dispenseTypeId = Number(dispenseTypeRule.xrefValue);
          inv.dispenseType = this.getDispenseType(inv.dispenseTypeId);
        }

        const settings = AbsgArrayHelper.filter(response.containerInfo, x => (x.containerId === item.positionContainerId ||
          x.containerId === item.stationContainerId) &&
          x.ruleId === Rules.positionLevelRules.witnessRequired);

        if (!AbsgArrayHelper.isNil(settings) && settings.length > 0) {
          const stationWitnessSetting = AbsgArrayHelper.find((settings), x => x.containerId === item.stationContainerId);
          const postionWitnessSetting = AbsgArrayHelper.find((settings), x => x.containerId === item.positionContainerId);

          if (!AbsgArrayHelper.isNil(stationWitnessSetting) && stationWitnessSetting.xrefValue === StringConstants.boolY) {
            if (!AbsgArrayHelper.isNil(postionWitnessSetting) && postionWitnessSetting.xrefValue === StringConstants.boolY) {
              inv.isWitness = Constants.onText;
            } else if (!AbsgArrayHelper.isNil(witnessRequiredSetting) && witnessRequiredSetting.value) {
              inv.isWitness = Constants.onText;
            }
          }
        }
        stationItems.push(inv);
      }
    });

    return AbsgArrayHelper.sortBy(stationItems, (item) => [item.clinicName, item.stationName]);
  }

  clearCachedUserStationAccessData(): void {
    this.cacheService.set(CacheKeys.userStationAccess, null);
    this.cacheService.set(CacheKeys.practiceStationsLite, null);
    this.cacheService.set(CacheKeys.practiceAnnexStationsLite, null);
  }

  hasItemMultipleLots(item: InventoryItem): boolean {
    if (item.inventoryItemProducts.length > 1) {
      if (!(AbsgArrayHelper.filter(item.inventoryItemProducts, x => x.lotQuantity <= 0).length > 0)) {
        return true;
      }
    }
    return false;
  }

  syncInventoryLots(positionContainerId: number): Observable<boolean> {
    if (positionContainerId > 0) {
      const positionContainer: PositionContainer = {
        positionContainerId,
      };
      return this.inventoryApi.syncStationLotTrackingItems(positionContainer)
        .pipe(take(1),
          concatMap(savedSuccessfully => {
            if (savedSuccessfully) {
              return this.refreshInventoryCacheAsync();
            }
            return of(savedSuccessfully);
          }),
          map(() => true));
    } else {
      return of(true);
    }
  }

  processE2EClearPendingQuantity(): void {
    this.inventoryApi.resetStationPendingQty()
      .pipe(
        take(1),
        tap(() => this.alertService.showSuccessAlert('Delete Done', 'Pending Quantity Cleared')),
      )
      .subscribe(() => { },
        (error: Error) => {
          this.alertService.showErrorAlert('Clear pending quantity failed.', error);
          this.handleError(error);
        },
      );
  }

  processE2EDeleteStationAssignments(): void {
    this.inventoryApi.deleteStationAssignments()
      .pipe(
        take(1),
        tap(() => this.alertService.showSuccessAlert('Delete Done', 'E2E delete assignments worked.')),
        concatMap(() => this.refreshInventoryCacheAsync()),
      )
      .subscribe(() => { },
        (error: Error) => {
          this.alertService.showErrorAlert('Delete station assignment failed', error);
          this.handleError(error);
        },
      );
  }

  processE2EDeleteStationPOs(): void {
    this.purchasingApi.deleteStationPos()
      .pipe(
        take(1),
        tap(() => this.alertService.showSuccessAlert('Delete Done', 'Station POs deleted')),
      )
      .subscribe(() => { },
        (error: Error) => {
          this.alertService.showErrorAlert('Delete station POs failed', error);
          this.handleError(error);
        },
      );
  }

  processE2ETwoStepPOApprovalProcess(isOn: boolean): Observable<UpdateStationsRuleValueRequest> {
    const finalAssignedStations: StationRuleValue = {
      containerId: this.getStationId(),
      ruleValue: isOn ? StringConstants.boolY : StringConstants.boolN,
    };

    const stationUpdateForRule: UpdateStationsRuleValueRequest = {
      ruleId: +Rules.stationLevelRules.useFinalPOApproval,
      stationRuleValues: [finalAssignedStations],
    };

    // Api call for final PO rule update
    return this.ruleContainerSettingApi.setContainersRuleValue(stationUpdateForRule).pipe(
      take(1),
      catchError((error: Error) => {
        this.alertService.showErrorAlert('Update two step PO approval failed', error);
        this.handleError(error);
        return of({});
      }));
  }

  getStations(): Observable<Station[]> {
    // Why are we not sending a param to limit it, this seems like it will pull all, which is not safe or secure
    return this.stationODataApi.getAllStationListOData()
      .pipe(
        map((stations: ODataResult<Station>) => stations.value as Station[]),
        catchError(() => of([])),
      );
  }

  getPracticeStations(): Observable<StationLite[]> {
    const cachedPracticeStationLite = this.cacheService.get(CacheKeys.practiceStationsLite);
    if (cachedPracticeStationLite.value) {
      return of(cachedPracticeStationLite.value);
    } else {
      return this.stationApi.getStationListByPractice()
        .pipe(
          map((stations) => {
            this.cacheService.set(CacheKeys.practiceStationsLite, stations);
            return stations as StationLite[];
          }),
          catchError(() => of([])),
        );
    }
  }

  getAnnexStationsByPractice(): Observable<StationLite[]> {
    const cachedPracticeAnnexStationLite = this.cacheService.get(CacheKeys.practiceAnnexStationsLite);
    if (cachedPracticeAnnexStationLite.value) {
      return of(cachedPracticeAnnexStationLite.value);
    }
    return this.stationApi.getAnnexStationsByPractice()
      .pipe(
        map((stations) => {
          this.cacheService.set(CacheKeys.practiceAnnexStationsLite, stations);
          return stations as StationLite[];
        }),
        catchError((error: Error) => {
          this.handleErrorMessage(error?.message);
          return of([]);
        }),
      );
  }

  checkAnnexItemsModalMode(annexStations: StationLite[], stationId: number): boolean {
    if (annexStations.length <= 1) {
      if (annexStations.length === 0 || (annexStations[0].stationId === stationId)) {
        return false;
      }
    }
    return true;
  }

  isAccessPointEmpty(accessPoint: StorageAccessPoint): boolean {
    // accessPoint for the UAC will NEVER be empty since there aren't any positions to fill
    return !(accessPoint && accessPoint.children &&
      this.getInventoryItemsByAccessPointId(accessPoint.parentContainerId, accessPoint.id).length > 0)
      && accessPoint.name.toLowerCase() !== stationConstants.UAC.toLowerCase();
  }

  clearStationRules(): void {
    this.ruleService.clearStationContainerRulesFromCache();
  }

  generateGetCheapestEquivilantRequest(excludeList: number[]): GetCheapestEquivilantRequest {
    return {
      accountNumber: this.station.getValue().acctNumber,
      excludeSupplierInclusionProductIds: excludeList,
    } as GetCheapestEquivilantRequest;
  }

  convertAssignedProductDetailToProductDetail(assignedProducts: AssignedProductDetail[]): ProductDetail[] {
    return AbsgArrayHelper.map(assignedProducts, (assignedItem) => ({
      displayName: assignedItem.displayName,
      alternateName: assignedItem.alternateName,
      dispenseUnitOfMeasureConversionFactor: assignedItem.dFactor,
      inventoryUnitOfMeasureConversionFactor: assignedItem.iFactor,
      purchaseUnitOfMeasureConversionFactor: assignedItem.pFactor,
      purchaseUnitOfMeasureSize: assignedItem.punitSize,
      volumeUnitOfMeasureConversionFactor: assignedItem.vFactor,
      purchaseUnitOfMeasureDisplay: assignedItem.pUomDisplay,
      dispenseUnitOfMeasureDisplay: assignedItem.dUomDisplay,
      inventoryUnitOfMeasureDisplay: assignedItem.iUomDisplay,
      volumeUnitOfMeasureDisplay: assignedItem.vUomDisplay,
      dispenseTypeId: assignedItem.dispenseTypeId,
      dispenseTypeName: LookupHelper.getDispenseTypeName(assignedItem.dispenseTypeId),
      genericName: assignedItem.genericName,
      tradeName: assignedItem.tradeName,
      gcnSequenceNumber: assignedItem.gcnSequenceNumber,
      is340B: assignedItem.is340B,
      isAuditFavorite: assignedItem.isAuditFavorite,
      formularyTypeId: assignedItem.formularyTypeId,
      isBuyIn: assignedItem.isBuyIn,
      isDiscontinued: assignedItem.isDiscontinued,
      isPracticeFavorite: assignedItem.isPracticeFavorite,
      itemId: assignedItem.itemId,
      meiParentId: assignedItem.meiParentId,
      packageNdc: assignedItem.packageNdc,
      unitNdc: assignedItem.unitNdc,
      jCode: assignedItem.jCode,
      productId: assignedItem.productId,
      refrigerated: assignedItem.refrigerated,
      singleDoseVial: assignedItem.singleDoseVial,
      supplierPartyId: assignedItem.supplierPartyId,
      supplierProductId: assignedItem.supplierProductId,
      supplierProductInclusionId: assignedItem.supplierProductInclusionId,
      altJCode: assignedItem.altJCode,
      ndc: assignedItem.packageNdc,
      isItem505b2: assignedItem.isItem505b2,
    } as ProductDetail));
  }

  private getCabinet(cabinetId: number): StorageCabinet {
    const cabinet = AbsgArrayHelper.filter(this.station.getValue().cabinets, cbt => cbt.id === cabinetId)[0];
    if (cabinet === null || cabinet === undefined) {
      const message = `Cabinet Id: ${cabinetId} Cabinet Not Found`;
      this.handleErrorMessage(message);
      throw new Error(message);
    }
    return cabinet;
  }

  private getInventoryItemsForContainer(accessPointDetails: StorageAccessPoint[]): InventoryItemSummary[] {
    const inventoryItems = this.getAssignedInventoryItems().getValue();
    const positionDetails = AbsgArrayHelper.reduce(accessPointDetails,
      (positionPoints: StoragePosition[], accessPoint: StorageAccessPoint) => positionPoints.concat(accessPoint.children), [])
      .filter(pd => pd !== undefined && pd !== null);
    const cabinetPositionsById = AbsgArrayHelper.groupBy(positionDetails, p => p.id);
    return AbsgArrayHelper.filter(inventoryItems, i => cabinetPositionsById.get(i.positionContainerId.toString()) !== undefined);
  }

  private getDispenseType(dispenseTypeId: number): string {
    return LookupHelper.getDispenseTypeName(dispenseTypeId);
  }

  private mapContainerDetails(requests: InventoryItemUth[]): RequestContainerDetails {
    const request: RequestContainerDetails = {};
    request.positionContainerIds = AbsgArrayHelper.map(requests, x => x.positionContainerId);
    request.stationContainerIds = AbsgArrayHelper.map(requests, x => x.stationContainerId);
    return request;
  }

  getPackageDirectory(): string {
    return this.packageDirectory;
  }

  getIsPackaged(): boolean {
    return this.isPackaged;
  }
}
